import useAddPhoto from '@components/features/orders/hooks/useAddPhoto';
import useAttachPhoto from '@components/features/orders/hooks/useAttachPhoto';
import PhotoPreviewModal from '@components/pages/orderPage/components/PhotoPreviewModal';
import { ModalState, SetModalState } from '@js/store/modalAtoms';
import { selectedPhotoAtom } from '@js/store/orderPageAtoms';
import { getBase64ImageFromUrl } from '@utils/fileUtils';
import { useAtom } from '@store';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export type AddPhotoPreviewModalData = {
    orderId: number | null;
};

export const initialAddPhotoPreviewModalState: ModalState<AddPhotoPreviewModalData> =
    {
        isOpen: false,
        isHidden: false,
        data: {
            orderId: null,
        },
    };

const AddPhotoPreviewModal = ({
    modalState,
    setModalState,
}: {
    modalState: ModalState<AddPhotoPreviewModalData>;
    setModalState: SetModalState<ModalState<AddPhotoPreviewModalData>>;
}) => {
    const { mutateAsync: addPhoto } = useAddPhoto();
    const { mutateAsync: attachPhotos } = useAttachPhoto();
    const [selectedPhoto] = useAtom(selectedPhotoAtom);

    const { t } = useTranslation();

    const handleConfirm = async () => {
        try {
            if (!modalState.data || !selectedPhoto) {
                throw new Error('No data provided');
            }

            const base64Photo = await getBase64ImageFromUrl(selectedPhoto.url);
            const { photoId } = await addPhoto({
                filename: selectedPhoto.filename,
                imageBase64: base64Photo,
            });

            if (modalState.data.orderId) {
                await attachPhotos({
                    orderId: modalState.data.orderId,
                    photoIds: [photoId],
                });
            }

            toast.success(t('addingPhotoSuccess', { ns: 'orders' }));
        } catch {
            toast.error(t('addingPhotoError', { ns: 'orders' }));
        }
    };

    return (
        <PhotoPreviewModal
            state={modalState}
            setState={setModalState}
            onConfirm={handleConfirm}
            title={t('confirmAddingPhoto', { ns: 'orders' })}
            confirmButtonText={t('confirmAddingPhoto', { ns: 'orders' })}
        />
    );
};

export default AddPhotoPreviewModal;
